import i18n, { LANGUAGE_STORAGE_KEY } from "../i18n";
import createLightTheme from "../assets/themes/lightTheme";
import { CssBaseline, ThemeProvider } from "@material-ui/core";
import { createContext, useContext, useEffect, useMemo, useState } from "react";

const LanguageContext = createContext({
	language: "en",
	direction: "ltr",
	setLanguage: () => {},
	switchLanguage: () => {},
});

export const useLanguage = () => useContext(LanguageContext);

export const LanguageProvider = ({ children }) => {
	const getInitialLanguage = () => {
		if (typeof window === "undefined") return "en";
		const urlParam = new URLSearchParams(window.location.search).get("language");
		if (urlParam === "ar" || urlParam === "en") {
			window.localStorage.setItem(LANGUAGE_STORAGE_KEY, urlParam);
			return urlParam;
		}
		const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
		return stored || i18n.resolvedLanguage || "en";
	};

	const [language, setLanguageState] = useState(getInitialLanguage);
	const direction = language === "ar" ? "rtl" : "ltr";

	useEffect(() => {
		i18n.changeLanguage(language);
		if (typeof document !== "undefined") {
			document.documentElement.lang = language;
			document.body.setAttribute("dir", direction);
		}
	}, [language, direction]);

	const setLanguage = (lng) => {
		const normalized = lng === "ar" ? "ar" : "en";
		setLanguageState(normalized);
		if (typeof window !== "undefined") {
			window.localStorage.setItem(LANGUAGE_STORAGE_KEY, normalized);
		}
	};

	const switchLanguage = () => {
		setLanguage(language === "ar" ? "en" : "ar");
	};

	const theme = useMemo(() => createLightTheme(direction), [direction]);

	const value = useMemo(
		() => ({ language, direction, setLanguage, switchLanguage, theme }),
		[language, direction, theme],
	);

	return (
		<LanguageContext.Provider value={value}>
			<ThemeProvider theme={theme}>
				<CssBaseline />
				{children}
			</ThemeProvider>
		</LanguageContext.Provider>
	);
};
